import React, { Fragment, memo } from 'react';
import { Link } from '@vkontakte/vkui';
import { getWikiSyntaxTree, PairTagChildrenParserResult, SyntaxTree } from './helpers/getWikiSyntaxTree';
import { trimSyntaxTree } from './helpers/trimSyntaxTree';

function makeReactNodeFromSyntaxTree(tree: SyntaxTree, tagReplacers: Record<string, React.ComponentType> = {}) {
  const extendedTagReplacers: Record<string, React.ComponentType> = { a: Link, ...tagReplacers };
  const result: React.ReactNode[] = [];

  for (let i = 0; i < tree.length; i++) {
    const syntaxNode = tree[i];

    if (typeof syntaxNode === 'string') {
      result.push(<React.Fragment key={i}>{syntaxNode}</React.Fragment>);
    } else {
      result.push(
        React.createElement(
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          syntaxNode.tagName ? extendedTagReplacers[syntaxNode.tagName] || syntaxNode.tagName : Fragment,
          { ...syntaxNode.attributes, key: i },
          syntaxNode.children ? makeReactNodeFromSyntaxTree(syntaxNode.children) : void 0,
        ),
      );
    }
  }

  return result;
}

export function makePlainReactNodeFromSyntaxTree(tree: SyntaxTree, ignoreNewLine = true) {
  const result: string[] = [];

  for (let i = 0; i < tree.length; i++) {
    const syntaxNode = tree[i];

    if (typeof syntaxNode === 'string') {
      result.push(syntaxNode);
    } else if (syntaxNode.children) {
      result.push(...makePlainReactNodeFromSyntaxTree(syntaxNode.children));
    } else if (syntaxNode.tagName === 'br' && !ignoreNewLine) {
      result.push('\n');
    } else {
      result.push(' ');
    }
  }

  return result;
}

interface FormatWikiContentProps {
  /** Текст с разметкой */
  children: string;
  /** Если передан true, то на выходе будет текст без разметки и без форматирования (удобно для показа превью) */
  plain?: boolean;
  /** Возможность пост-обработать href в ссылках. Может быть полезно для away.php ссылок */
  processHref?: (href: string) => string;
  /** Дополнительный парсер ссылок, отработает если дефолтный считает, что ссылка невалидная */
  additionalLinkParser?: ([url, text]: string[]) => PairTagChildrenParserResult;
  /** Заменяет теги на компоненты */
  tagReplacers?: Record<string, React.ComponentType>;
}

const asIs = (value: string) => value;

/**
 * Парсер вики-разметки ВКонтакте.
 * Поддерживаемые правила форматирования см. в сторибуке.
 */
export const FormatWikiContent = memo(function FormatWikiContent(props: FormatWikiContentProps) {
  const { children, plain, processHref = asIs, additionalLinkParser, tagReplacers } = props;

  const wikiSyntaxTree = trimSyntaxTree(getWikiSyntaxTree(children.trim(), processHref, additionalLinkParser));

  if (plain) {
    return <>{makePlainReactNodeFromSyntaxTree(wikiSyntaxTree)}</>;
  }

  return <>{makeReactNodeFromSyntaxTree(wikiSyntaxTree, tagReplacers)}</>;
});
